import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface ImageInfo {
  fileName: string;
  uploadDate: string;
  fileSize: number;
  isActive: boolean;
  downloadUrl?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ImageService {
  private baseUrl = environment.dataApiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Upload multiple images for an entity
   * @param entityType - The type of entity (e.g., 'accident')
   * @param entityId - The ID of the entity
   * @param files - Array of files to upload
   * @returns Observable with upload result
   */
  uploadImages(entityType: string, entityId: string, files: File[]): Observable<any> {
    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append(`files`, file, file.name);
    });

    return this.http.post(
      `${this.baseUrl}/${entityType}/${entityId}/images/upload`,
      formData
    );
  }

  /**
   * Get all images for an entity
   * @param entityType - The type of entity (e.g., 'accident')
   * @param entityId - The ID of the entity
   * @param activeOnly - Whether to get only active images
   * @returns Observable with array of image info
   */
  getImages(entityType: string, entityId: string): Observable<ImageInfo[]> {
    return this.http.get<ImageInfo[]>(`${this.baseUrl}/${entityType}/${entityId}/images`);
  }

  /**
   * Delete an image
   * @param entityType - The type of entity (e.g., 'accident')
   * @param entityId - The ID of the entity
   * @param fileName - The name of the file to delete
   * @returns Observable with deletion result
   */
  deleteImage(
    entityType: string,
    entityId: string,
    fileName: string
  ): Observable<string> {
    return this.http.delete(
      `${
        this.baseUrl
      }/${entityType}/${entityId}/images/delete?fileName=${encodeURIComponent(fileName)}`,
      {
        responseType: 'text',
      }
    );
  }

  /**
   * Get download URL for an image
   * @param entityType - The type of entity (e.g., 'accident')
   * @param entityId - The ID of the entity
   * @param fileName - The name of the file to download
   * @returns Observable with download URL
   */
  getDownloadUrl(
    entityType: string,
    entityId: string,
    fileName: string
  ): Observable<Blob> {
    return this.http.get(
      `${
        this.baseUrl
      }/${entityType}/${entityId}/images/download?fileName=${encodeURIComponent(
        fileName
      )}`,
      {
        responseType: 'blob',
      }
    );
  }

  /**
   * Get image as data URL for display
   * @param entityType - The type of entity (e.g., 'accident')
   * @param entityId - The ID of the entity
   * @param fileName - The name of the file
   * @returns Observable with data URL
   */
  getImageAsDataUrl(
    entityType: string,
    entityId: string,
    fileName: string
  ): Observable<string> {
    return new Observable((observer) => {
      this.getDownloadUrl(entityType, entityId, fileName).subscribe({
        next: (blob) => {
          const reader = new FileReader();
          reader.onload = () => {
            observer.next(reader.result as string);
            observer.complete();
          };
          reader.onerror = (error) => {
            observer.error(error);
          };
          reader.readAsDataURL(blob);
        },
        error: (error) => {
          observer.error(error);
        },
      });
    });
  }
}
